import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, Copy, CheckCircle2, MessageCircle, Globe, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const TEXTS = {
  en: {
    title: "Payment Instructions",
    greeting: "Hello! Please follow the steps below to process your payment.",
    step1: "Download TransferGo",
    step1desc: "Download the TransferGo app from the App Store or Google Play Store.",
    step2: "Create an Account",
    step2desc: "Sign up and verify your TransferGo account.",
    step3: 'Click "Add/Create Receiver"',
    step3desc: "Fill in the receiver information below:",
    bankDetails: "Bank Details",
    recipientName: "Recipient Name",
    bankName: "Bank Name",
    accountNumber: "Account Number",
    city: "City",
    postalCode: "Postal Code",
    additionalInfo: "If additional information is required",
    firstName: "First Name",
    lastName: "Last Name",
    copied: "Copied to clipboard!",
    afterPayment: "After Payment",
    afterPaymentMsg: "Please send the English version of your payment receipt to our WhatsApp number for payment verification.",
    whatsappBtn: "Send Receipt via WhatsApp",
    whatsappNumber: "+63 960 740 941",
    backToBooking: "Book a Trial Class",
    langSwitch: "Turkce",
  },
  tr: {
    title: "Odeme Talimatlari",
    greeting: "Merhaba! Odemenizi tamamlamak icin asagidaki adimlari takip edin.",
    step1: "TransferGo Indirin",
    step1desc: "TransferGo uygulamasini App Store veya Google Play'den indirin.",
    step2: "Hesap Olusturun",
    step2desc: "TransferGo hesabinizi olusturun ve dogrulayin.",
    step3: '"Alici Ekle" ye tiklayin',
    step3desc: "Asagidaki alici bilgilerini girin:",
    bankDetails: "Banka Bilgileri",
    recipientName: "Alici Adi",
    bankName: "Banka Adi",
    accountNumber: "Hesap Numarasi",
    city: "Sehir",
    postalCode: "Posta Kodu",
    additionalInfo: "Ek bilgi gerekirse",
    firstName: "Ad",
    lastName: "Soyad",
    copied: "Panoya kopyalandi!",
    afterPayment: "Odeme Sonrasi",
    afterPaymentMsg: "Lutfen odeme dekontunuzun Ingilizce versiyonunu dogrulama icin WhatsApp numaramiza gonderin.",
    whatsappBtn: "Dekontu WhatsApp ile Gonderin",
    whatsappNumber: "+63 960 740 941",
    backToBooking: "Deneme Dersi Ayirt",
    langSwitch: "English",
  }
};

function CopyField({ label, value }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success("Copied!"); };
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="font-semibold text-slate-800">{value}</p>
      </div>
      <button onClick={copy} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-teal-600 transition-colors">
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PaymentInstructions() {
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState(searchParams.get("lang") || "en");
  const t = TEXTS[lang];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Language toggle + back */}
        <div className="flex items-center justify-between">
          <a href="/book-trial" className="flex items-center gap-1.5 text-teal-600 hover:text-teal-800 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> {t.backToBooking}
          </a>
          <button onClick={() => setLang(lang === "en" ? "tr" : "en")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-teal-200 text-teal-700 hover:bg-teal-50 text-sm font-medium"
            data-testid="payment-lang-toggle">
            <Globe className="w-4 h-4" /> {t.langSwitch}
          </button>
        </div>

        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">{t.title}</h1>
          <p className="text-slate-500 mt-2">{t.greeting}</p>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {[
            { num: 1, title: t.step1, desc: t.step1desc },
            { num: 2, title: t.step2, desc: t.step2desc },
            { num: 3, title: t.step3, desc: t.step3desc },
          ].map(s => (
            <div key={s.num} className="flex gap-4 items-start">
              <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">{s.num}</div>
              <div>
                <p className="font-semibold text-slate-800">{s.title}</p>
                <p className="text-sm text-slate-500">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bank Details */}
        <Card className="border-0 shadow-lg" data-testid="bank-details">
          <div className="bg-teal-600 px-5 py-3 rounded-t-lg">
            <h2 className="text-white font-semibold">{t.bankDetails}</h2>
          </div>
          <CardContent className="p-5">
            <CopyField label={t.recipientName} value="Luxury Media Impacts Digital Marketing Services" />
            <CopyField label={t.bankName} value="Union Bank of the Philippines" />
            <CopyField label={t.accountNumber} value="002790027669" />
            <CopyField label={t.city} value="Bacolod" />
            <CopyField label={t.postalCode} value="6100" />
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card className="border-0 shadow-md" data-testid="additional-info">
          <div className="bg-slate-600 px-5 py-3 rounded-t-lg">
            <h2 className="text-white font-semibold text-sm">{t.additionalInfo}</h2>
          </div>
          <CardContent className="p-5">
            <CopyField label={t.firstName} value="Esmeralda" />
            <CopyField label={t.lastName} value="Castillo" />
            <CopyField label={t.city} value="Bacolod" />
          </CardContent>
        </Card>

        {/* After Payment */}
        <Card className="border-0 shadow-lg border-t-4 border-t-amber-400" data-testid="after-payment">
          <CardContent className="p-5 text-center space-y-4">
            <CheckCircle2 className="w-10 h-10 text-amber-500 mx-auto" />
            <h3 className="font-bold text-slate-800">{t.afterPayment}</h3>
            <p className="text-sm text-slate-600">{t.afterPaymentMsg}</p>
            <a href="https://wa.me/63960740941?text=Hi%2C%20I%20would%20like%20to%20send%20my%20payment%20receipt%20for%20verification."
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              data-testid="whatsapp-receipt-btn">
              <MessageCircle className="w-5 h-5" /> {t.whatsappBtn}
            </a>
            <p className="text-xs text-slate-400">{t.whatsappNumber}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
